import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { Subject, firstValueFrom } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller, Viol3Status } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { ArbetsorderEventStore, ArbetsorderEventStoreInfo } from "~app/features/shared/services/arbetsorderEventStore.service";
import { AvslutaAvtalsobjektCommand } from "../../commands/AvslutaAvtalsobjektCommand";
import { IArbetsorderDetail } from "../../planera-detail/planera-detail-models";

@Component({
    selector: "rask-arbetsorder-viol3-makulera-button",
    templateUrl: "./arbetsorder-viol3-makulera-button.component.html",
    styleUrls: ["./arbetsorder-viol3-makulera-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ButtonComponent]
})
export class ArbetsorderViol3MakuleraButtonComponent implements OnInit, OnDestroy {
    @Input() arbetsorder: IArbetsorderDetail;
    isSending: boolean;
    statusProduktion: string;
    statusTransport: string;

    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly unsubscribe$ = new Subject<void>();

    get canAvsluta(): boolean {
        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        if (this.isSending) {
            return false;
        }

        const allowMakuleraStatuses = [
            Viol3Status.Aktiv,
            Viol3Status.Fel,
            Viol3Status.AktivUppdateras
        ];

        if (allowMakuleraStatuses.some(x => x === this.statusProduktion) || allowMakuleraStatuses.some(x => x === this.statusTransport)) {
            return true;
        }

        return false;
    }

    avsluta() {
        const message = "Avtalobjektet kommer att avslutas och ett nytt utkast skapas."
            + " Eventuella ändringar till avtalsobjektet kommer att skrivas över. Tryck Avsluta avtalsobjekt eller Avbryt";

        const settings = new ModalSettings("Avsluta avtalsobjekt", message);
        settings.yesCaption = "Avsluta avtalsobjekt";
        settings.noCaption = "Avbryt";
        firstValueFrom(this.raskModalService.confirm(settings))
            .then((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const command = new AvslutaAvtalsobjektCommand((x) => {
                        x.arbetsorderId = this.arbetsorder.id;
                    });

                    // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
                    const local = this.commandService.createInstance();
                    local.addTransactionCommand(command);

                    this.busyService
                        .addWork(local.pushCommandQueueTransaction())
                        .subscribe();
                }
            });
    }

    ngOnDestroy() {
        this.arbetsorderEventStore.removeArbetsorderList([this.arbetsorder.id]);
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit() {
        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorder.id]);
        this.arbetsorderEventStore.arbetsorderStoreEventsLoaded$
            .pipe(takeUntil(this.unsubscribe$))
            .pipe(filter(x => x.has(this.arbetsorder.id)), map(z => z.get(this.arbetsorder.id)))
            .subscribe(y => {
                this.setDataFromStore(y);
            });

        this.arbetsorderEventStore.arbetsorderSynchFinished$
            .pipe(takeUntil(this.unsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(x => {
                this.setDataFromStore(x);
            });

        this.arbetsorderEventStore.arbetsorderSendStatusChanged$.pipe(takeUntil(this.unsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(y => {
                this.setDataFromStore(y);
            });
    }

    private setDataFromStore(y: ArbetsorderEventStoreInfo) {
        this.statusProduktion = y.statusProduktion;
        this.statusTransport = y.statusTransport;
        this.isSending = y.arbetsorderIsSending;
    }
}
